/**
 * OP-MOTION lanes (2026-10-23, OPM4LBuilder's ask "OP-Motion, a new device on the submixes and Main that your
 * timelines drive", `OPApps/opbuttons-opm4lbuilder.md`). Their operator: "when i press a timeline with a hi cut eq on
 * it, the hi cut auto dynamically sweeps down to the set point on bar 1 ... and then follows the timeline until the
 * end". What the device offers is DATA here (their feature freeze: "keep the target and Move lists as data"), with how
 * a lane's 0-127 points read in a target's own units, and the compile to segments.
 *
 * THE WIRE (built and tested in Live by OPM4LBuilder, v0.1): OSC to `127.0.0.1:11320` on the computer running Live,
 * which every instance hears and filters by its bus.
 *   /OP-Motion/seg <bus|all> <target> <end> <dur_beats> <shape 0-4> <hold_beats>
 *   /OP-Motion/off <bus|all> <target|all> <tail_beats> <shape>
 *   /OP-Motion/flick <master|all>
 *
 * FOUR FACTS OF THE DEVICE'S ENGINE that shape the compile (read in `staging/op-motion-gen.mjs`, 2026-10-23):
 *   1. A seg glides from WHEREVER the effect is to `end`; it has no "from". So where a run of points starts (a section
 *      start, which a loop or a jump re-enters) the compile first glides to the start value over a quarter beat, then
 *      sends the segment a quarter beat later: two messages in one audio block collapse into the last one, and
 *      OPBridge sends a launch's first line up to 5% of a bar late, all at once.
 *   2. ANY seg takes the effect back from a hand (last touch wins), so nothing is re-sent to keep a lane alive.
 *   3. A lane's lease is (dur + hold) beats + 2 s; `hold` is the beats from arriving to the lane's next message, so a
 *      held value is never mistaken for a dead sender, and a stopped timeline glides off on its own once it lapses.
 *   4. The crossfader (`xfade`) has no lease: a fade to B stays at B.
 */

import {
  leadInSectionId, timelineLoops, timelinePlaySections,
  type AutomationPointStep, type MotionShape, type OpMotionCrossfadeStep, type Timeline, type TimelineLane,
} from '@opbuttons/schema';
import { autoValueAtExact, shapeAt } from './automation.ts';

export const MOTION_OSC_TARGET = '127.0.0.1:11320';
export const MOTION_SEG_ADDRESS = '/OP-Motion/seg';
export const MOTION_OFF_ADDRESS = '/OP-Motion/off';
export const MOTION_FLICK_ADDRESS = '/OP-Motion/flick';

/** The device's buses, in its own words (`master` is Live's Main). */
export const MOTION_BUSES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'vocals', label: 'Vocals' },
  { id: 'bvs', label: 'BVs' },
  { id: 'music', label: 'Music' },
  { id: 'bass', label: 'Bass' },
  { id: 'drums', label: 'Drums' },
  { id: 'master', label: 'Main' },
];

export type MotionFamily = 'Effect' | 'Polish' | 'Build' | 'Exit' | 'Crossfader';

export interface MotionTargetDef {
  id: string;
  label: string;
  family: MotionFamily;
  /** -1..1 with the middle off (Tone, the crossfader); otherwise 0..1. */
  bipolar: boolean;
  /** One plain line: what moving it does. */
  hint: string;
  /** The words for the bottom and the top of a bipolar lane. */
  low?: string;
  high?: string;
  /** Only the OP-Motion on Main honours it. */
  mainOnly?: boolean;
}

/** Every target OP-Motion v0.1 takes, grouped as the device groups them (Moves by family: Polish, Build, Exit). */
export const MOTION_TARGETS: readonly MotionTargetDef[] = [
  { id: 'tone', label: 'Tone', family: 'Effect', bipolar: true, low: 'darker', high: 'thinner', hint: 'Down darkens it (a hi-cut), up thins it (a lo-cut); the middle is off' },
  { id: 'air', label: 'Air', family: 'Effect', bipolar: false, hint: 'Sparkle on top (a high shelf); at the bottom it is off' },
  { id: 'breathe', label: 'Breathe', family: 'Polish', bipolar: false, hint: 'A slow swell of air with a touch of darkness: subtle movement' },
  { id: 'lift', label: 'Lift', family: 'Polish', bipolar: false, hint: 'Opens up the top, for a section that lifts' },
  { id: 'build', label: 'Build', family: 'Build', bipolar: false, hint: 'Thins the sound out as it rises, for a build' },
  { id: 'darken', label: 'Darken', family: 'Exit', bipolar: false, hint: 'Pulls the top off, for a breakdown or an outro' },
  { id: 'xfade', label: 'Crossfader', family: 'Crossfader', bipolar: true, low: 'A', high: 'B', mainOnly: true, hint: "Live's crossfader: down is A, up is B" },
];

/** The Move families in the order the picker shows them. */
export const MOVE_FAMILIES: readonly MotionFamily[] = ['Polish', 'Build', 'Exit'];

export function motionTarget(id: string | undefined): MotionTargetDef | undefined {
  return MOTION_TARGETS.find((t) => t.id === id);
}

/** A lane's point (0-127) in the target's units: 0..1, or -1..1 with 64 EXACTLY the middle (so "off" is reachable). */
export function motionValue(t: MotionTargetDef, v127: number): number {
  const v = Math.max(0, Math.min(127, v127));
  const x = t.bipolar ? (v <= 64 ? (v - 64) / 64 : (v - 64) / 63) : v / 127;
  return Math.round(x * 1000) / 1000;
}

/** A value in the target's units, in words: "off", "40%", "30% darker", "A", "75% to B". */
export function motionValueText(t: MotionTargetDef, v127: number): string {
  const x = motionValue(t, v127);
  const pct = Math.round(Math.abs(x) * 100);
  if (t.id === 'xfade') return x <= -1 ? 'A' : x >= 1 ? 'B' : pct === 0 ? 'the middle' : `${pct}% to ${x < 0 ? 'A' : 'B'}`;
  if (t.bipolar) return pct === 0 ? 'off' : `${pct}% ${x < 0 ? t.low : t.high}`;
  return pct === 0 ? 'off' : `${pct}%`;
}

/** The device's number for a shape. */
export const SHAPE_INT: Record<MotionShape, number> = { s: 0, riser: 1, fall: 2, linear: 3, late: 4 };

/** Every way a point travels to the next, as the editor offers them (the device's five, then a hold). */
export const CURVE_CHOICES: ReadonlyArray<{ id: NonNullable<AutomationPointStep['curve']>; label: string; hint: string }> = [
  { id: 's', label: 'S-curve', hint: 'eases out and in' },
  { id: 'riser', label: 'Riser', hint: 'slow, then fast' },
  { id: 'fall', label: 'Fall-away', hint: 'fast, then settles' },
  { id: 'linear', label: 'Straight', hint: 'evenly' },
  { id: 'late', label: 'Late cut', hint: 'waits, then moves' },
  { id: 'hold', label: 'Hold, then jump', hint: 'stays, then jumps' },
];

export function curveLabel(c: AutomationPointStep['curve']): string {
  return CURVE_CHOICES.find((x) => x.id === (c ?? 'linear'))?.label ?? 'Straight';
}

/** The glide back to off where the timeline ends, as offered. */
export const GLIDE_OUT_CHOICES: ReadonlyArray<{ id: 'none' | 'beat' | 'bar' | '2bars'; label: string }> = [
  { id: 'none', label: 'None' },
  { id: 'beat', label: '1 beat' },
  { id: 'bar', label: '1 bar' },
  { id: '2bars', label: '2 bars' },
];

/** A lane's glide-out, its absent value resolved (one bar; the crossfader none: "off" sends it home). */
export function motionGlideOut(lane: TimelineLane): 'none' | 'beat' | 'bar' | '2bars' {
  return lane.motion?.glideOut ?? (lane.motion?.target === 'xfade' ? 'none' : 'bar');
}

/** The buses a lane sends to, as the device names them: `all`, or the picked ones in the device's order. The
 *  crossfader is Main's alone. */
export function motionBusesOf(lane: TimelineLane): string[] {
  const m = lane.motion;
  if (!m) return [];
  if (motionTarget(m.target)?.mainOnly) return ['master'];
  if (m.buses.length === 0 || m.buses.includes('all')) return ['all'];
  const picked = MOTION_BUSES.filter((b) => m.buses.includes(b.id)).map((b) => b.id);
  return picked.length > 0 ? picked : ['all'];
}

/** "Tone on Music and Bass", "Crossfader on Main", "Build on every bus". */
export function motionDrivesText(lane: TimelineLane): string {
  const t = motionTarget(lane.motion?.target);
  if (!t) return 'OP-Motion (an unknown target)';
  const buses = motionBusesOf(lane);
  const where = buses[0] === 'all' ? 'every bus' : buses.map((b) => MOTION_BUSES.find((x) => x.id === b)?.label ?? b).join(', ');
  return `${t.label} on ${where}`;
}

/** A sensible lane name for a target and its buses. */
export function motionLaneName(target: string, buses: string[]): string {
  const t = motionTarget(target);
  if (!t) return 'OP-Motion';
  if (t.mainOnly) return t.label;
  const one = buses.length === 1 && buses[0] !== 'all' ? MOTION_BUSES.find((b) => b.id === buses[0])?.label : undefined;
  return one ? `${t.label} (${one})` : t.label;
}

/** One message the lane sends, before the bus fan-out. */
export interface MotionMessage {
  /** Bars from the timeline's start. */
  at: number;
  /** The step it comes from, for ids and warnings. */
  stepId: string;
  kind: 'seg' | 'off';
  /** seg: the end value in the target's units; off: unused. */
  end: number;
  /** The same on the lane's 0-127 scale (off: where off is), for drawing. */
  end127: number;
  /** seg: beats to get there (0 = jump); off: the tail. */
  beats: number;
  shape: number;
  /** seg: beats from arriving to the lane's next message (the lease). */
  hold: number;
}

/** Beats in a timeline bar, as OPBridge schedules one: it counts every bar as 4 of Live's beats whatever the time
 *  signature (`BEATS_PER_BAR`, `crates/opbridge-timelines/src/lib.rs:78`, read 2026-10-23). A segment's beats must be
 *  the time between the cues OPBridge sends, so the compile uses this, never the signature: in 4/4 they agree. */
export const OPBRIDGE_BEATS_PER_BAR = 4;

/** Live's beats (quarter notes) in one bar of a time signature: what "over 4 bars" means on a tap. */
export function liveBeatsPerBar(num: number | undefined, den: number | undefined): number {
  const n = num && Number.isFinite(num) && num > 0 ? num : 4;
  const d = den && Number.isFinite(den) && den > 0 ? den : 4;
  return (n * 4) / d;
}

/** A quarter beat: the gap between gliding to a run's start value and its first segment (engine fact 1). */
const START_BEATS = 0.25;
const EPS = 1e-6;
const r4 = (x: number): number => Math.round(x * 10000) / 10000;

/**
 * A lane's messages, section by section in the order the timeline lays them out. Each section is self-contained,
 * because OPBridge can enter it by a loop or a jump as well as by playing into it: where the curve has a value at the
 * section's start, the section starts by gliding there (a quarter beat), and a ramp that runs past the section's end
 * is cut there and taken up again at the next section's start. Nothing is sent in the lead-in (before the first
 * section), as for every other cue. Where the timeline ENDS (a section that stops, or the last section when neither
 * it nor the timeline loops), the lane glides off over its glide-out, landing on the end.
 */
export function motionMessages(t: Timeline, lane: TimelineLane): MotionMessage[] {
  const target = motionTarget(lane.motion?.target);
  if (!target) return [];
  const pts = lane.steps
    .filter((x): x is AutomationPointStep => x.kind === 'automationPoint')
    .map((x) => ({ ...x, value: Math.max(0, Math.min(127, x.value)) }))
    .sort((a, b) => a.position - b.position);
  if (pts.length === 0) return [];
  const bpb = OPBRIDGE_BEATS_PER_BAR;
  // Before the first point the lane is off: it has no value, unlike a control's lane.
  const valueAt = (bar: number): number | null => (bar < pts[0]!.position - EPS ? null : autoValueAtExact(pts, bar));
  // The shape of the stretch a bar falls in: the curve of the last point at or before it.
  const curveAt = (bar: number): AutomationPointStep['curve'] => {
    let c: AutomationPointStep['curve'] = undefined;
    for (const p of pts) { if (p.position <= bar + EPS) c = p.curve; else break; }
    return c;
  };
  const tail = { none: 0, beat: 1, bar: bpb, '2bars': 2 * bpb }[motionGlideOut(lane)];
  const secs = timelinePlaySections(t);
  const lead = leadInSectionId(t);
  const out: MotionMessage[] = [];
  let s0 = 0;
  secs.forEach((sec, i) => {
    const s1 = s0 + sec.lengthBars;
    const start = s0;
    s0 = s1;
    if (sec.id === lead || sec.lengthBars <= 0) return;
    const ends = sec.onEnd === 'stop' || (i === secs.length - 1 && sec.onEnd !== 'loop' && !timelineLoops(t));
    const anyHere = valueAt(s1 - EPS) !== null;
    const offAt = ends && tail > 0 && anyHere ? Math.max(start, s1 - tail / bpb) : null;
    const cutoff = offAt ?? s1;
    // The points that play in this section: its start (where the curve has a value there), every point inside, and
    // where a moving stretch is cut off.
    type E = { pos: number; v: number; curve: AutomationPointStep['curve']; id: string; mark?: true };
    const E: E[] = [];
    const v0 = valueAt(start);
    const atStart = pts.find((p) => Math.abs(p.position - start) < EPS);
    if (v0 !== null) E.push({ pos: start, v: atStart ? atStart.value : v0, curve: atStart ? atStart.curve : curveAt(start), id: atStart?.id ?? `${lane.id}@${start}` });
    for (const p of pts) if (p.position > start + EPS && p.position < cutoff - EPS) E.push({ pos: p.position, v: p.value, curve: p.curve, id: p.id });
    const last = E[E.length - 1];
    if (last && (last.curve ?? 'linear') !== 'hold' && pts.some((p) => p.position > last.pos + EPS && p.position >= cutoff - EPS)) {
      const vc = valueAt(cutoff);
      if (vc !== null) E.push({ pos: cutoff, v: vc, curve: undefined, id: `${last.id}~cut`, mark: true });
    }
    const here: MotionMessage[] = [];
    for (let k = 0; k < E.length; k++) {
      const p = E[k]!;
      if (p.mark) break;
      const next = E[k + 1];
      const gapBeats = next ? (next.pos - p.pos) * bpb : Infinity;
      let segAt = p.pos;
      const seg = (at: number, end: number, beats: number, shape: number) =>
        here.push({ at, stepId: p.id, kind: 'seg', end: motionValue(target, end), end127: end, beats: r4(beats), shape, hold: 0 });
      if (k === 0) {
        // A run starts: glide to its value, then the segment a quarter beat later (or halfway, when the next point
        // is closer than half a beat).
        const lead = Math.min(START_BEATS, gapBeats / 2);
        seg(p.pos, p.v, lead, SHAPE_INT.linear);
        segAt = p.pos + lead / bpb;
      } else if ((E[k - 1]!.curve ?? 'linear') === 'hold') {
        // Hold, then jump: the jump, then the segment out of it a quarter beat later.
        seg(p.pos, p.v, 0, SHAPE_INT.linear);
        segAt = p.pos + Math.min(START_BEATS, gapBeats / 2) / bpb;
      }
      const c = p.curve ?? 'linear';
      if (next && c !== 'hold' && next.pos > segAt + EPS) seg(segAt, next.v, (next.pos - segAt) * bpb, SHAPE_INT[c]);
    }
    if (offAt !== null) here.push({ at: offAt, stepId: last?.id ?? `${lane.id}@end`, kind: 'off', end: 0, end127: motionOff127(target), beats: r4(tail), shape: SHAPE_INT.s, hold: 0 });
    // Each segment's hold: from arriving to the lane's next message, or to where the section is cut off.
    here.sort((a, b) => a.at - b.at);
    here.forEach((m, j) => {
      if (m.kind !== 'seg') return;
      const nextAt = here.slice(j + 1).find((n) => n.at > m.at + EPS)?.at ?? cutoff;
      m.hold = r4(Math.max(0, (nextAt - m.at) * bpb - m.beats));
    });
    out.push(...here);
  });
  return out;
}

/** The crossfade buttons' fade lengths in bars, as offered (OPM4LBuilder's panel has the same idea: FADE over BARS). */
export const CROSSFADE_BARS = [1, 2, 4, 8, 16] as const;
export const CROSSFADE_DEFAULT_BARS = 4;
/** A row's cells, in the order the row reads: to A, to the centre, to B (left, centre, right, as the operator says
 *  them: "a glide to L C or R"), then the flick. Before 2026-10-24 the row read A, Flick, B. */
export const CROSSFADE_ACTIONS = ['fadeA', 'fadeC', 'fadeB', 'flick'] as const;
export type CrossfadeAction = (typeof CROSSFADE_ACTIONS)[number];

/** Where a fade goes on Live's crossfader (-1 = A, 0 = the centre, 1 = B) and its side's letter; null for the flick. */
export function crossfadeSide(action: CrossfadeAction): { value: -1 | 0 | 1; letter: 'A' | 'C' | 'B' } | null {
  switch (action) {
    case 'fadeA': return { value: -1, letter: 'A' };
    case 'fadeC': return { value: 0, letter: 'C' };
    case 'fadeB': return { value: 1, letter: 'B' };
    case 'flick': return null;
  }
}
/** "To A", "To C", "To B", or "Flick": a crossfade cell's name, everywhere it is shown. */
export const crossfadeName = (action: CrossfadeAction): string => { const s = crossfadeSide(action); return s ? `To ${s.letter}` : 'Flick'; };
/** "A", "the centre", "B": where a fade goes, in a sentence. */
export const crossfadeWhere = (action: CrossfadeAction): string => { const s = crossfadeSide(action); return !s ? 'the other side' : s.letter === 'C' ? 'the centre' : s.letter; };

/** What a crossfade step sends (`OpMotionCrossfadeStep`): `flick` to the other side now, or a fade to A (-1), the
 *  centre (0) or B (+1) over its bars with its shape and no hold (the crossfader has no lease). `beatsPerBar`: on a
 *  timeline OPBridge's bar (4); on a tap, Live's own bar from its time signature (`liveBeatsPerBar`). */
export function crossfadeSend(step: OpMotionCrossfadeStep, beatsPerBar: number): { address: string; args: Array<number | string> } {
  const side = crossfadeSide(step.action);
  if (!side) return { address: MOTION_FLICK_ADDRESS, args: ['master'] };
  const bars = step.bars !== undefined && Number.isFinite(step.bars) && step.bars > 0 ? step.bars : CROSSFADE_DEFAULT_BARS;
  return {
    address: MOTION_SEG_ADDRESS,
    args: ['master', 'xfade', side.value, r4(bars * Math.max(1, beatsPerBar)), SHAPE_INT[step.shape ?? 's'], 0],
  };
}

/** "4 bars", "1 bar", "half a bar". */
export function barsWords(bars: number): string {
  if (bars === 0.5) return 'half a bar';
  return `${bars} bar${bars === 1 ? '' : 's'}`;
}

/** Where "off" sits on a lane's 0-127 scale: the middle of Tone, the bottom of the rest, and the crossfader's home (A,
 *  its default). */
export function motionOff127(t: MotionTargetDef): number {
  return t.bipolar && t.id !== 'xfade' ? 64 : 0;
}

const SHAPE_OF_INT: readonly MotionShape[] = ['s', 'riser', 'fall', 'linear', 'late'];

/** What OP-Motion plays for this lane when the timeline is played straight through (no loops or jumps), on the lane's
 *  0-127 scale, for drawing it as it will sound: every section's start, the quarter-beat starts, the cuts and the
 *  glide-out. Nothing before the first point (the effect is off there). `total` is the timeline's length in bars. */
export function motionPlayedPath(t: Timeline, lane: TimelineLane, total: number): Array<{ bar: number; v: number }> {
  const target = motionTarget(lane.motion?.target);
  const msgs = motionMessages(t, lane);
  if (!target || msgs.length === 0) return [];
  const bpb = OPBRIDGE_BEATS_PER_BAR;
  const off = motionOff127(target);
  const out: Array<{ bar: number; v: number }> = [];
  let v = off;
  msgs.forEach((m, j) => {
    const next = Math.max(m.at, msgs[j + 1]?.at ?? total);
    const start = v;
    const end = m.kind === 'off' ? off : m.end127;
    const durBars = m.beats / bpb;
    const shape = SHAPE_OF_INT[m.shape] ?? 'linear';
    if (durBars <= EPS) {
      out.push({ bar: m.at, v: start }, { bar: m.at, v: end });
      v = end;
    } else {
      const run = Math.min(next - m.at, durBars);
      const n = shape === 'linear' ? 1 : Math.max(4, Math.ceil(run * 24));
      for (let k = 0; k <= n; k++) {
        const bar = m.at + (run * k) / n;
        out.push({ bar, v: start + (end - start) * shapeAt(shape, (bar - m.at) / durBars) });
      }
      v = start + (end - start) * shapeAt(shape, run / durBars);
    }
    if (next > (out[out.length - 1]?.bar ?? m.at) + EPS) out.push({ bar: next, v });
  });
  return out;
}

/** The OSC action each message becomes, once per bus. */
export function motionOscArgs(lane: TimelineLane, m: MotionMessage, bus: string): Array<number | string> {
  const target = lane.motion?.target ?? '';
  return m.kind === 'seg' ? [bus, target, m.end, m.beats, m.shape, m.hold] : [bus, target, m.beats, m.shape];
}

// ─── Landing presets (2026-10-24, OPM4LBuilder's ask; OP-Motion v0.3-rc4) ─────────────────────────────────────────
//   /OP-Motion/capture <bus|all> <id>             each matching instance replies once (one with no bus stays quiet)
//   reply /OP-Motion/state <bus> <id> <name> <value> ...   to 127.0.0.1:7049 (OPBridge's OSC input), same machine
//   /OP-Motion/land <bus|all> <name> <value> ...  every named value glides from where it is, ARRIVING ON THE NEXT
//                                                 DOWNBEAT (the device works it out, so it is sent AT the press)

export const MOTION_CAPTURE_ADDRESS = '/OP-Motion/capture';
export const MOTION_LAND_ADDRESS = '/OP-Motion/land';
export const MOTION_STATE_ADDRESS = '/OP-Motion/state';
/** The `pwa_action` command OPBridge's rule turns a `/OP-Motion/state` reply into (`midiTrigger/ruleSync.ts`). */
export const MOTION_STATE_COMMAND = 'opmotion_state';

/** A bus as the operator reads it: "Music", "Main", "every bus". */
export function motionBusLabel(bus: string): string {
  return bus === 'all' ? 'every bus' : MOTION_BUSES.find((b) => b.id === bus)?.label ?? bus;
}

/** A lane's value (0-127) at `bar`, or null before its first point (the effect is off there) or with no points. The
 *  same reading `motionMessages` starts a section with. */
export function motionValueAt127(lane: TimelineLane, bar: number): number | null {
  const pts = lane.steps
    .filter((x): x is AutomationPointStep => x.kind === 'automationPoint')
    .map((x) => ({ ...x, value: Math.max(0, Math.min(127, x.value)) }))
    .sort((a, b) => a.position - b.position);
  if (pts.length === 0 || bar < pts[0]!.position - EPS) return null;
  const exact = pts.find((p) => Math.abs(p.position - bar) < EPS);
  return exact ? exact.value : autoValueAtExact(pts, bar);
}

/**
 * THE GLIDE IN FROM THE PRESS (answer 19 (a) as adapted 2026-10-23, `OPApps/opbuttons-opm4lbuilder.md`): what the
 * press sends so every lane ARRIVES at its value on `bar` on the downbeat the run lands on. One `/OP-Motion/land` per
 * bus, holding every lane's `<target> <value>` for that bus (the device glides each from where it is and works the
 * downbeat out itself, so it is sent AT the press). One message per bus, never one per lane: two messages reaching an
 * instance in one audio block collapse into the last (engine fact 1), so a bus that two lanes drive would keep only
 * one. For the same reason `all` is spelled out bus by bus when another lane names a bus of its own. A lane with no
 * value at `bar` (before its first point) sends nothing: the device stays where it is, as the section's own cues do.
 */
export function motionLandsAt(lanes: readonly TimelineLane[], bar: number): Array<{ address: string; args: Array<string | number> }> {
  const each: Array<{ buses: string[]; target: string; value: number }> = [];
  for (const lane of lanes) {
    const target = motionTarget(lane.motion?.target);
    const v = target ? motionValueAt127(lane, bar) : null;
    if (!target || v === null) continue;
    each.push({ buses: motionBusesOf(lane), target: target.id, value: motionValue(target, v) });
  }
  if (each.length === 0) return [];
  const spellAll = each.some((e) => !e.buses.includes('all'));
  const byBus = new Map<string, Array<string | number>>();
  for (const e of each) {
    for (const bus of e.buses.flatMap((b) => (b === 'all' && spellAll ? MOTION_BUSES.map((x) => x.id) : [b]))) {
      const pairs = byBus.get(bus) ?? [];
      // A later lane on the same bus and target wins, as it would in the device.
      const at = pairs.indexOf(e.target);
      if (at >= 0 && at % 2 === 0) pairs.splice(at, 2);
      pairs.push(e.target, e.value);
      byBus.set(bus, pairs);
    }
  }
  const known = MOTION_BUSES.map((b) => b.id);
  return [...byBus.keys()]
    .sort((a, b) => (known.indexOf(a) < 0 ? 99 : known.indexOf(a)) - (known.indexOf(b) < 0 ? 99 : known.indexOf(b)) || a.localeCompare(b))
    .map((bus) => ({ address: MOTION_LAND_ADDRESS, args: [bus, ...byBus.get(bus)!] }));
}

/** A stored pair list made safe to send: names are strings, values numbers or strings, an odd tail dropped. */
export function landPairs(raw: ReadonlyArray<unknown>): Array<string | number> {
  const out: Array<string | number> = [];
  for (let i = 0; i + 1 < raw.length; i += 2) {
    const name = raw[i];
    const value = raw[i + 1];
    if (typeof name !== 'string' || name === '') continue;
    if (typeof value === 'number' && Number.isFinite(value)) out.push(name, value);
    else if (typeof value === 'string') out.push(name, value);
  }
  return out;
}

/** What landing a preset sends: one `land` per bus it holds, the device's buses in its own order, then any other. */
export function landSends(preset: { buses: Record<string, ReadonlyArray<unknown>> }): Array<{ address: string; args: Array<string | number> }> {
  const known = MOTION_BUSES.map((b) => b.id);
  const buses = Object.keys(preset.buses).sort((a, b) => {
    const ia = known.indexOf(a), ib = known.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b);
  });
  const out: Array<{ address: string; args: Array<string | number> }> = [];
  for (const bus of buses) {
    const pairs = landPairs(preset.buses[bus] ?? []);
    if (pairs.length > 0) out.push({ address: MOTION_LAND_ADDRESS, args: [bus, ...pairs] });
  }
  return out;
}

/** A `/OP-Motion/state` reply's args as `{bus, id, pairs}`, or null when it is not one. */
export function parseMotionState(args: unknown): { bus: string; id: number; pairs: Array<string | number> } | null {
  if (!Array.isArray(args) || args.length < 2) return null;
  const bus = args[0];
  const id = Number(args[1]);
  if (typeof bus !== 'string' || bus === '' || !Number.isFinite(id)) return null;
  return { bus, id, pairs: landPairs(args.slice(2)) };
}

const pct = (x: number): string => `${Math.round(Math.abs(x) * 100)}%`;

/** One bus of a preset in words: "Tone 30% darker, sine at 1/4; Air 20%". Values at rest (0, motion off) are left
 *  out; a name this app does not know is shown as it came. */
export function motionPairsText(pairs: ReadonlyArray<string | number>): string {
  const m = new Map<string, string | number>();
  for (let i = 0; i + 1 < pairs.length; i += 2) m.set(String(pairs[i]), pairs[i + 1]!);
  const parts: string[] = [];
  const done = new Set<string>();
  for (const fx of ['tone', 'air', 'pan']) {
    const v = Number(m.get(fx));
    const motion = String(m.get(`${fx}.motion`) ?? 'off');
    const depth = Number(m.get(`${fx}.depth`));
    for (const k of [fx, `${fx}.motion`, `${fx}.rate`, `${fx}.depth`]) done.add(k);
    const words: string[] = [];
    if (m.has(fx) && Number.isFinite(v) && Math.abs(v) > 0.005) {
      words.push(fx === 'tone' ? `${pct(v)} ${v < 0 ? 'darker' : 'thinner'}` : pct(v));
    }
    if (motion !== 'off') words.push(`${motion} at ${String(m.get(`${fx}.rate`) ?? 'bar')}${Number.isFinite(depth) ? `, ${pct(depth)} deep` : ''}`);
    if (words.length > 0) parts.push(`${fx[0]!.toUpperCase()}${fx.slice(1)} ${words.join(', ')}`);
  }
  for (const [k, v] of m) {
    if (done.has(k)) continue;
    const n = Number(v);
    if (typeof v === 'number' && Math.abs(n) <= 0.005) continue;
    const t = motionTarget(k);
    parts.push(t ? `${t.label} ${typeof v === 'number' ? pct(n) : v}` : `${k} ${v}`);
  }
  return parts.length > 0 ? parts.join('; ') : 'everything at rest';
}
